import React from 'react';
import { CheckCircle2, Lock, ShieldCheck } from 'lucide-react';

/**
 * StripeRedirectOverlay — Full-screen elegant interstitial shown for ~1s
 * between successful registration and the redirect to checkout.stripe.com.
 *
 * Eliminates the "wait, what site is this?" moment for first-time users by
 * making it explicit that they're being handed off to Stripe.
 */

const StripeWordmark = ({ className = '' }) => (
  <span
    className={`font-bold tracking-tight ${className}`}
    style={{ color: '#635BFF', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}
  >
    stripe
  </span>
);

const StripeRedirectOverlay = ({ message = 'Preparing your secure checkout…' }) => {
  return (
    <div
      data-testid="stripe-redirect-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-md mx-4 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8 sm:p-10 shadow-[0_30px_90px_-20px_rgba(212,175,55,0.25)]">
        {/* Logo handoff: NoFee → Stripe */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="text-2xl font-black tracking-tight">
            <span className="text-white">No</span>
            <span className="bg-gradient-to-b from-gold-400 to-gold-600 bg-clip-text text-transparent">
              Fee
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span className="w-1 h-1 rounded-full bg-white/70" />
          </div>
          <StripeWordmark className="text-2xl" />
        </div>

        {/* Success check */}
        <div className="flex items-center justify-center mb-5">
          <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" strokeWidth={2.2} />
          </div>
        </div>

        <h2 className="text-center text-xl font-black text-white">
          Account created
        </h2>
        <p className="mt-2 text-center text-sm text-white/60">
          {message}
        </p>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-1.5 mt-6">
          <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>

        {/* Trust line */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-[11px] text-white/45">
          <span className="inline-flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> 256-bit SSL
          </span>
          <span className="hidden sm:block w-px h-3 bg-white/10" />
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> PCI DSS Level 1
          </span>
          <span className="hidden sm:block w-px h-3 bg-white/10" />
          <span>Trusted by millions</span>
        </div>
      </div>
    </div>
  );
};

export default StripeRedirectOverlay;
